import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { YoutubeLogo, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export const VideoCard = ({ video, testid }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <article data-testid={testid} className="border border-border bg-white transition-shadow duration-200 hover:shadow-[0_2px_18px_rgba(15,23,42,0.07)]">
      <div className="aspect-video w-full bg-black">
        {playing ? (
          <iframe
            className="h-full w-full"
            src={`${video.embed_url}?autoplay=1&rel=0`}
            title={video.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid={`${testid}-iframe`}
          />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} data-testid={`${testid}-play`} className="group relative h-full w-full">
            <img src={video.thumbnail_url} alt={video.title} className="h-full w-full object-cover" loading="lazy" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors duration-200 group-hover:bg-black/40">
              <YoutubeLogo size={44} weight="fill" className="text-white" />
            </span>
          </button>
        )}
      </div>
      <div className="p-4">
        <a href={video.url} target="_blank" rel="noopener noreferrer" className="tap-safe text-sm font-semibold text-[#0F172A] hover:text-[#002FA7] hover:underline">
          {video.title}
        </a>
        {video.published && <p className="mt-1 text-xs text-slate-500">{String(video.published).slice(0, 10)}</p>}
        {playing && (
          <a href={video.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-fallback`} className="mt-2 inline-block text-xs text-slate-500 hover:text-[#002FA7] hover:underline">
            Not playing? Watch it on YouTube
          </a>
        )}
      </div>
    </article>
  );
};

/** Latest uploads from the workspace YouTube channel. */
export const VideoStrip = ({ limit = 3, heading = "Watch", showAll = true, testid = "video-strip" }) => {
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState(null);

  useEffect(() => {
    api.get(`/videos/latest?limit=${limit}`).then(({ data: d }) => setData(d)).catch(() => setData({ items: [] }));
    api.get("/videos/channel").then(({ data: d }) => setChannel(d)).catch(() => {});
  }, [limit]);

  if (!data || data.items.length === 0) return null;

  return (
    <section data-testid={testid} className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[#B3241B]">From the channel</p>
          <h2 className="mt-1 text-base font-semibold text-[#0F172A] md:text-lg">{heading}</h2>
        </div>
        <div className="flex items-center gap-4">
          {channel?.subscribe_url && (
            <a href={channel.subscribe_url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-subscribe`} className="inline-flex items-center gap-1.5 bg-[#B3241B] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-[#8E1C15]">
              <YoutubeLogo size={15} weight="fill" /> Subscribe
            </a>
          )}
          {showAll && (
            <Link to="/videos" data-testid={`${testid}-all`} className="tap-safe inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#002FA7] hover:underline">
              All videos <ArrowRight size={13} />
            </Link>
          )}
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.items.map((v) => <VideoCard key={v.video_id} video={v} testid={`${testid}-video-${v.video_id}`} />)}
      </div>
    </section>
  );
};
